const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})/

const ordinalSuffix = (day: number) => {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th'

  return { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] ?? 'th'
}

// Today as a YYYY-MM-DD calendar date in London. launch_date is a
// merchandising calendar date with no time or zone, set by a London team, so
// it's compared against London's date rather than the shopper's: every
// shopper flips from Coming Soon at the same moment (London midnight) instead
// of US shoppers seeing it hours longer. Assembled from parts rather than a
// locale's own format, so the two compare as plain YYYY-MM-DD strings.
function getLondonToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(datePart => datePart.type === type)?.value

  return `${part('year')}-${part('month')}-${part('day')}`
}

// The launch date to promise when a colourway is Coming Soon, else null —
// so the one value is both the flag and the date the message shows. Coming
// Soon is a launch date still in the future (strictly — on launch day it goes
// on sale), whatever stock the colourway holds: a few units can be in before
// launch (photoshoot samples) and still not be for sale.
export function getComingSoonLaunchDate(
  launchDate: string | null | undefined,
  today = getLondonToday()
): string | null {
  const match = launchDate?.match(CALENDAR_DATE)

  if (!match) return null

  return match[0] > today ? match[0] : null
}

// A YYYY-MM-DD launch date as "21st October", the same shape as the made to
// order dispatch date. Read from its parts rather than through a Date, which
// would parse it as UTC midnight and could land on the day before.
export function formatLaunchDate(launchDate: string): string {
  const [, year, month, day] = launchDate.match(CALENDAR_DATE) ?? []
  const dayOfMonth = Number(day)
  const monthName = new Date(
    Date.UTC(Number(year), Number(month) - 1, dayOfMonth)
  ).toLocaleDateString('en-GB', {
    month: 'long',
    timeZone: 'UTC'
  })

  return `${dayOfMonth}${ordinalSuffix(dayOfMonth)} ${monthName}`
}
